import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { ArrowRight, Phone, Target, Briefcase, Building, CircleCheck as CheckCircle } from 'lucide-react';

const Referenzen = () => {
  const seoJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Referenzen & Fallbeispiele – IT-Dienstleistungen Mönchengladbach',
    description: 'Praxisbeispiele: Wie KMU ihre IT verbessert haben. Microsoft 365, Digitalisierung, IT-Sicherheit. Ehrliche Fallbeispiele ohne Namen.',
    url: 'https://schteffann.de/referenzen'
  };

  const fallbeispiele = [
    {
      branche: 'Handwerksbetrieb',
      groesse: '12 Mitarbeiter',
      ausgangssituation: 'Alte Server im Büro, keine vernünftigen Backups, Monteure hatten keinen Zugriff auf aktuelle Pläne und Fotos. E-Mail-Chaos, jeder speicherte Dateien wo anders.',
      massnahmen: [
        'Migration zu Microsoft 365 mit SharePoint',
        'Strukturierte Projektordner: Ein Ordner pro Baustelle',
        'Teams-App auf Monteur-Handys eingerichtet',
        'Automatische Backups in der Cloud',
        'Einfache Schulung für das Team'
      ],
      ergebnis: 'Monteure können jetzt direkt vom Handy auf Pläne und Fotos zugreifen. Büro hat alles im Überblick. Keine verlorenen Dateien mehr, deutlich weniger E-Mails.',
      dauer: '3 Wochen',
      kosten: 'Einmalig + monatliche Microsoft 365 Kosten'
    },
    {
      branche: 'Steuerberatung',
      groesse: '5 Mitarbeiter',
      ausgangssituation: 'Mandantendaten auf lokalem Server, umständliche Freigaben, keine klare Struktur. Datenschutz-Unsicherheit, keine geprüfte Backup-Strategie.',
      massnahmen: [
        'Cloud-Server mit verschlüsselten Verbindungen',
        'Klare Zugriffsrechte pro Mandant',
        'Tägliche Backups mit monatlichen Wiederherstellungs-Tests',
        'Passwortmanager für alle Mitarbeiter',
        'DSGVO-konforme Dokumentation'
      ],
      ergebnis: 'Mandantendaten sicher gespeichert, klare Zugriffskontrolle, nachweisbare Backups. Team fühlt sich sicherer, Datenschutz erfüllt.',
      dauer: '4 Wochen',
      kosten: 'Einmalig + monatliche Hosting-Kosten'
    },
    {
      branche: 'Pflegedienst',
      groesse: '18 Mitarbeiter',
      ausgangssituation: 'Dienstpläne in Excel, per E-Mail verschickt. Änderungen gingen unter, niemand hatte den aktuellen Stand. Viel Telefonieren, viele Missverständnisse.',
      massnahmen: [
        'Microsoft Planner für Dienstpläne',
        'Teams-Channels für verschiedene Standorte',
        'Schulung in kleinen Gruppen',
        'Schritt-für-Schritt-Anleitungen erstellt'
      ],
      ergebnis: 'Alle sehen sofort den aktuellen Dienstplan. Änderungen werden direkt sichtbar. Deutlich weniger Anrufe, weniger Chaos.',
      dauer: '2 Wochen',
      kosten: 'Einmalig + bestehende Microsoft 365 Lizenz genutzt'
    },
    {
      branche: 'Architekturbüro',
      groesse: '7 Mitarbeiter',
      ausgangssituation: 'Große CAD-Dateien per E-Mail verschickt, Versionschaos, unklare Freigaben. Kein strukturiertes Backup, alte Festplatten als "Sicherung".',
      massnahmen: [
        'OneDrive Business mit ausreichend Speicher',
        'SharePoint-Projektordner mit Versionierung',
        'Automatische Synchronisierung auf allen Rechnern',
        'Externe Backup-Lösung eingerichtet',
        'Dateibenennung vereinheitlicht'
      ],
      ergebnis: 'Keine E-Mail-Anhänge mehr, jeder arbeitet an der aktuellen Version. Automatisches Backup läuft. Deutlich weniger Stress bei Projektübergaben.',
      dauer: '3 Wochen',
      kosten: 'Einmalig + erweiterte Microsoft 365 Lizenz'
    },
    {
      branche: 'Werbeagentur',
      groesse: '4 Mitarbeiter',
      ausgangssituation: 'Jeder hatte sein eigenes System. Kundendaten in verschiedenen Tools, keine gemeinsame Ablage. Angebotserstellung dauerte ewig.',
      massnahmen: [
        'Gemeinsame Kundenablage in SharePoint',
        'Angebots-Vorlage mit Microsoft Forms',
        'Power Automate für automatische Angebotserstellung',
        'Vereinfachte Prozesse dokumentiert'
      ],
      ergebnis: 'Angebote werden jetzt in 5 Minuten statt 30 Minuten erstellt. Alle finden Kundendaten sofort. Weniger Fehler, mehr Zeit für kreative Arbeit.',
      dauer: '2 Wochen',
      kosten: 'Einmalig, keine zusätzlichen Lizenzkosten'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEOHead
        title="Referenzen & Fallbeispiele – IT-Dienstleistungen Mönchengladbach | Schteffann"
        description="Praxisbeispiele: Wie KMU in Mönchengladbach ihre IT verbessert haben. Microsoft 365, Digitalisierung, IT-Sicherheit. Ehrliche Fallbeispiele ohne Namen."
        keywords="IT Referenzen Mönchengladbach, Fallbeispiele KMU, Microsoft 365 Erfahrungen, IT Digitalisierung Beispiele, Schteffann"
        jsonLd={seoJsonLd}
      />
      {/* Hero */}
      <section className="relative py-20 lg:py-24 overflow-hidden bg-gradient-to-br from-cyan-50/50 via-blue-50/30 to-slate-50">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-600/5"></div>
        <div className="container relative">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-800 border-cyan-200 px-6 py-2">
              <Briefcase className="w-4 h-4 mr-2" />
              Referenzen
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-slate-900">Praxisbeispiele </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-700">aus dem KMU-Alltag</span>
            </h1>
            <p className="text-xl lg:text-2xl text-slate-600 leading-relaxed">
              So haben andere Unternehmen ihre IT verbessert. Ehrliche Fallbeispiele, keine Namen, realistische Ergebnisse.
            </p>
          </div>
        </div>
      </section>

      {/* Fallbeispiele */}
      <section className="py-20">
        <div className="container">
          <div className="space-y-12">
            {fallbeispiele.map((fall, index) => (
              <Card key={index} className="border-slate-200 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-8 lg:p-12">
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="p-3 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl">
                      <Building className="h-6 w-6 text-cyan-600" />
                    </div>
                    <div>
                      <h2 className="text-2xl lg:text-3xl font-bold text-slate-900">{fall.branche}</h2>
                      <p className="text-slate-600">{fall.groesse}</p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Ausgangssituation</h3>
                      <p className="text-lg text-slate-700 leading-relaxed">{fall.ausgangssituation}</p>
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Was wir gemacht haben</h3>
                      <div className="space-y-2">
                        {fall.massnahmen.map((massnahme, idx) => (
                          <div key={idx} className="flex items-start space-x-2">
                            <CheckCircle className="h-5 w-5 text-cyan-600 mt-0.5 flex-shrink-0" />
                            <p className="text-slate-700">{massnahme}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Ergebnis</h3>
                      <p className="text-lg text-slate-900 font-medium leading-relaxed">{fall.ergebnis}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-200">
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Zeitrahmen</p>
                        <p className="text-slate-900 font-medium">{fall.dauer}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Kostenrahmen</p>
                        <p className="text-slate-900 font-medium">{fall.kosten}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Hinweis */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-cyan-50/30">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Datenschutz-Hinweis</h2>
            <p className="text-lg text-slate-700 leading-relaxed">
              Alle Fallbeispiele sind real, aber anonymisiert. Ich nenne grundsätzlich keine Kundennamen ohne explizite Zustimmung. Die beschriebenen Projekte sind typisch für meine Arbeit mit KMU.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container">
          <Card className="bg-gradient-to-br from-cyan-600 via-blue-600 to-cyan-700 border-0 shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
            <CardContent className="p-12 lg:p-16 relative text-white text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Ihr Projekt könnte das nächste sein
              </h2>
              <p className="text-xl text-cyan-100 mb-10 max-w-2xl mx-auto">
                Lassen Sie uns schauen, wie ich Ihnen helfen kann.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Button size="lg" asChild className="bg-white text-cyan-700 hover:bg-cyan-50 shadow-xl px-8 py-4 text-lg">
                  <Link to="/contact">
                    <Target className="mr-2 h-5 w-5" />
                    Jetzt Gespräch vereinbaren
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 text-lg">
                  <a href="tel:015679697252">
                    <Phone className="mr-2 h-5 w-5" />
                    0156-79697252
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Referenzen;
